/**
 * Suggest work-relevant words/phrases from Dispatch scope notes for the
 * Has Notes → Notes Contain filter chips.
 *
 * Pure / self-contained: scans primary notes + extra_data_sections bodies,
 * ranks frequent unigrams and bigrams, and drops common English filler so
 * chips surface terms like "HVAC" rather than "and" / "if".
 */

/** Common English / filler tokens that rarely help filter construction notes. */
const NOTES_PHRASE_STOP_WORDS = new Set([
  "a",
  "about",
  "after",
  "again",
  "all",
  "also",
  "am",
  "an",
  "and",
  "any",
  "are",
  "as",
  "at",
  "be",
  "been",
  "before",
  "being",
  "but",
  "by",
  "can",
  "could",
  "did",
  "do",
  "does",
  "doing",
  "done",
  "down",
  "during",
  "each",
  "few",
  "for",
  "from",
  "get",
  "got",
  "had",
  "has",
  "have",
  "having",
  "he",
  "her",
  "here",
  "hers",
  "him",
  "his",
  "how",
  "i",
  "if",
  "in",
  "into",
  "is",
  "it",
  "its",
  "just",
  "like",
  "me",
  "more",
  "most",
  "my",
  "need",
  "needs",
  "no",
  "not",
  "now",
  "of",
  "on",
  "once",
  "only",
  "or",
  "other",
  "our",
  "out",
  "over",
  "own",
  "please",
  "same",
  "she",
  "should",
  "so",
  "some",
  "still",
  "such",
  "than",
  "that",
  "the",
  "their",
  "them",
  "then",
  "there",
  "these",
  "they",
  "this",
  "those",
  "through",
  "to",
  "too",
  "under",
  "until",
  "up",
  "us",
  "very",
  "was",
  "we",
  "were",
  "what",
  "when",
  "where",
  "which",
  "while",
  "who",
  "will",
  "with",
  "would",
  "you",
  "your",
  // Soft filler common in field notes
  "also",
  "back",
  "come",
  "coming",
  "going",
  "make",
  "maybe",
  "per",
  "see",
  "take",
  "today",
  "tomorrow",
  "tonight",
  "use",
  "used",
  "using",
  "via",
  "week",
  "yesterday",
]);

/**
 * Collect note text blobs from scope rows (primary notes + extra section bodies).
 * Section titles are omitted so generic labels do not pollute suggestions.
 *
 * @param {Array<{ notes?: string|null, extra_data_sections?: Array<{ body?: string }>|null }>} rows
 * @returns {string[]}
 */
function collectDispatchNotesTexts(rows) {
  const texts = [];
  for (const row of rows ?? []) {
    const primary = row?.notes?.toString().trim();
    if (primary) {
      texts.push(primary);
    }
    for (const section of row?.extra_data_sections ?? []) {
      const body = section?.body?.toString().trim();
      if (body) {
        texts.push(body);
      }
    }
  }
  return texts;
}

/**
 * Split note text into raw tokens (letters/digits with optional inner - / ').
 *
 * @param {string} text
 * @returns {string[]}
 */
function tokenizeNotesText(text) {
  return (text.match(/[A-Za-z0-9]+(?:['’/-][A-Za-z0-9]+)*/g) ?? []).map(
    (token) => token.replace(/['’]/g, "")
  );
}

/**
 * Whether a token is worth counting as a note-filter suggestion.
 * Tokens of 3 characters or fewer are always excluded.
 *
 * @param {string} token
 * @returns {boolean}
 */
function isMeaningfulNotesToken(token) {
  if (!token) {
    return false;
  }
  const normalized = token.toLowerCase();
  if (normalized.length <= 3) {
    return false;
  }
  if (NOTES_PHRASE_STOP_WORDS.has(normalized)) {
    return false;
  }
  // Drop short pure numbers (e.g. years-ish codes still need length > 3 above).
  if (/^\d+$/.test(normalized)) {
    return false;
  }
  return true;
}

/**
 * Prefer a more "display-like" casing for a phrase (e.g. HVAC over hvac).
 *
 * @param {string|undefined} current
 * @param {string} candidate
 * @returns {string}
 */
function preferDisplayPhrase(current, candidate) {
  if (!current) {
    return candidate;
  }
  const currentUpper = current === current.toUpperCase();
  const candidateUpper = candidate === candidate.toUpperCase();
  if (candidateUpper && !currentUpper) {
    return candidate;
  }
  if (!candidateUpper && currentUpper) {
    return current;
  }
  // Prefer the variant with more uppercase letters (acronyms / titles).
  const upperScore = (value) =>
    [...value].filter((ch) => ch >= "A" && ch <= "Z").length;
  return upperScore(candidate) > upperScore(current) ? candidate : current;
}

/**
 * Scan Dispatch scope notes and return the most common work-relevant phrases.
 *
 * Ranks frequent single words and two-word phrases after stripping stop words.
 * Bigrams that appear at least twice are favored so suggestions tend toward
 * concrete work terms (e.g. "pour slab") over generic singles when both compete.
 *
 * @param {Array<Object>} rows Weekly WorkScopeAspect rows (or any note-bearing scopes).
 * @param {Object} [options]
 * @param {number} [options.limit=5] Max phrases to return.
 * @returns {Array<{ phrase: string, count: number }>} Top phrases, highest frequency first.
 */
export function suggestDispatchNotesFilterPhrases(rows, { limit = 5 } = {}) {
  const unigramCounts = new Map();
  const unigramDisplay = new Map();
  const bigramCounts = new Map();
  const bigramDisplay = new Map();

  const bump = (map, displayMap, key, display) => {
    map.set(key, (map.get(key) ?? 0) + 1);
    displayMap.set(key, preferDisplayPhrase(displayMap.get(key), display));
  };

  for (const text of collectDispatchNotesTexts(rows)) {
    const tokens = tokenizeNotesText(text).filter(isMeaningfulNotesToken);
    for (let i = 0; i < tokens.length; i += 1) {
      const token = tokens[i];
      const key = token.toLowerCase();
      bump(unigramCounts, unigramDisplay, key, token);

      if (i + 1 < tokens.length) {
        const next = tokens[i + 1];
        const bigramKey = `${key} ${next.toLowerCase()}`;
        bump(bigramCounts, bigramDisplay, bigramKey, `${token} ${next}`);
      }
    }
  }

  /** @type {Array<{ phrase: string, count: number, score: number }>} */
  const candidates = [];

  for (const [key, count] of unigramCounts) {
    candidates.push({
      phrase: unigramDisplay.get(key) ?? key,
      count,
      score: count,
    });
  }

  for (const [key, count] of bigramCounts) {
    // Prefer repeated phrases; single-occurrence bigrams are usually noise.
    if (count < 2) {
      continue;
    }
    candidates.push({
      phrase: bigramDisplay.get(key) ?? key,
      count,
      score: count * 1.75,
    });
  }

  candidates.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    if (b.count !== a.count) {
      return b.count - a.count;
    }
    return a.phrase.localeCompare(b.phrase, undefined, { sensitivity: "base" });
  });

  const picked = [];
  const usedUnigrams = new Set();

  for (const candidate of candidates) {
    if (picked.length >= limit) {
      break;
    }
    // Exclude any suggestion whose full phrase is 3 characters or shorter.
    if (candidate.phrase.replace(/\s+/g, "").length <= 3) {
      continue;
    }
    const parts = candidate.phrase.toLowerCase().split(/\s+/);
    // Skip a unigram already covered by a selected bigram (avoid "HVAC" + "HVAC duct").
    if (parts.length === 1 && usedUnigrams.has(parts[0])) {
      continue;
    }
    picked.push({ phrase: candidate.phrase, count: candidate.count });
    for (const part of parts) {
      usedUnigrams.add(part);
    }
  }

  return picked;
}
